<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Template
  Reason: Interactive QL-demo page (Side-Car Playground) for the Clean-Core External
          Side-Car pattern family. Selector row (party + type) + three demo cards;
          each card shows the exact QL DTO sent, the dynamic-column result table,
          and a collapsible Raw-JSON panel (TM-1000001960).
-->
@if (initialized) {
    <div class="flex flex-column gap-3 p-3">

        <!-- Page header -->
        <div class="flex flex-column gap-1">
            <h2 class="m-0">Side-Car Playground — QL</h2>
            <span class="text-color-secondary">
                Interaktive QL-Demos für das Clean-Core Side-Car-Muster: externe Flat-Felder (#4),
                externe typisierte Felder (#5) und interne typisierte Felder (#3).
            </span>
        </div>

        <!-- Selector load error -->
        @if (selectorError) {
            <p-message severity="error" [text]="selectorError" styleClass="w-full"></p-message>
        }

        <!-- Selector row -->
        <p-panel header="Auswahl">
            <div class="flex flex-wrap align-items-end gap-4">

                <!-- ExternalParty / Zuordnung selector -->
                <div class="flex flex-column gap-2">
                    <label for="xx-pg-assignment" class="font-medium">Externe Partei / Zuordnung</label>
                    <p-select
                            inputId="xx-pg-assignment"
                            [options]="assignments"
                            [(ngModel)]="selectedAssignment"
                            [showClear]="true"
                            placeholder="Zuordnung auswählen"
                            appendTo="body"
                            styleClass="w-20rem">
                        <ng-template let-assignment pTemplate="selectedItem">
                            @if (assignment) {
                                <span>{{ resolveAssignmentLabel(assignment) }}</span>
                            }
                        </ng-template>
                        <ng-template let-assignment pTemplate="item">
                            <span>{{ resolveAssignmentLabel(assignment) }}</span>
                        </ng-template>
                    </p-select>
                </div>

                <!-- XxPersonType selector -->
                <div class="flex flex-column gap-2">
                    <label for="xx-pg-type" class="font-medium">Personentyp</label>
                    <p-select
                            inputId="xx-pg-type"
                            [options]="personTypes"
                            [(ngModel)]="selectedPersonType"
                            [showClear]="true"
                            placeholder="Typ auswählen"
                            appendTo="body"
                            styleClass="w-20rem">
                        <ng-template let-type pTemplate="selectedItem">
                            @if (type) {
                                <span>{{ resolvePersonTypeLabel(type) }}</span>
                            }
                        </ng-template>
                        <ng-template let-type pTemplate="item">
                            <span>{{ resolvePersonTypeLabel(type) }}</span>
                        </ng-template>
                    </p-select>
                </div>

                @if (selectorsLoading) {
                    <div class="flex align-items-center gap-2">
                        <p-progressSpinner styleClass="w-2rem h-2rem" strokeWidth="4"></p-progressSpinner>
                        <span class="text-color-secondary">Auswahllisten werden geladen…</span>
                    </div>
                }
            </div>
        </p-panel>

        <!-- Card 1 — #4 External Flat -->
        <p-panel header="#4 External Flat — ein Join-Parameter (external_party_assignment_id)">
            <div class="flex flex-column gap-3">
                <span class="text-color-secondary">
                    Bounded-External-Flatten auf xx.XxPerson mit einem Join-Parameter. Die angeforderten
                    Feld-IDs werden zu ext_&lt;typeFieldId&gt;-Spalten abgeflacht.
                </span>

                <div>
                    <p-button
                            label="QL ausführen"
                            icon="fa-regular fa-play"
                            [disabled]="!selectedAssignment || flatCard.loading"
                            (onClick)="runFlatDemo()">
                    </p-button>
                </div>

                <ng-container
                        [ngTemplateOutlet]="cardResult"
                        [ngTemplateOutletContext]="{ card: flatCard }">
                </ng-container>
            </div>
        </p-panel>

        <!-- Card 2 — #5 External Typed -->
        <p-panel header="#5 External Typed — zwei Join-Parameter (external_party_assignment_id + type_id)">
            <div class="flex flex-column gap-3">
                <span class="text-color-secondary">
                    Bounded-External-Flatten auf xx.XxPerson mit zwei Join-Parametern (Zuordnung + Personentyp).
                    Die angeforderten Feld-IDs werden zu ext_&lt;typeFieldId&gt;-Spalten abgeflacht.
                </span>

                <div>
                    <p-button
                            label="QL ausführen"
                            icon="fa-regular fa-play"
                            [disabled]="!selectedAssignment || !selectedPersonType || typedCard.loading"
                            (onClick)="runTypedDemo()">
                    </p-button>
                </div>

                <ng-container
                        [ngTemplateOutlet]="cardResult"
                        [ngTemplateOutletContext]="{ card: typedCard }">
                </ng-container>
            </div>
        </p-panel>

        <!-- Card 3 — #3 Internal Typed -->
        <p-panel header="#3 Internal Typed — interne generische Attribute (gen_&lt;typeFieldId&gt;)">
            <div class="flex flex-column gap-3">
                <span class="text-color-secondary">
                    Lesen von xx.XxPersonData. Die generischen Attributwerte erscheinen als
                    gen_&lt;typeFieldId&gt;-Spalten. Ist ein Personentyp gewählt, wird danach gefiltert.
                </span>

                <div>
                    <p-button
                            label="QL ausführen"
                            icon="fa-regular fa-play"
                            [disabled]="internalCard.loading"
                            (onClick)="runInternalDemo()">
                    </p-button>
                </div>

                <ng-container
                        [ngTemplateOutlet]="cardResult"
                        [ngTemplateOutletContext]="{ card: internalCard }">
                </ng-container>
            </div>
        </p-panel>
    </div>
}

<!-- Reusable card-result block: QL DTO + result table + raw JSON -->
<ng-template #cardResult let-card="card">

    <!-- Error state -->
    @if (card.errorMessage) {
        <p-message severity="error" [text]="card.errorMessage" styleClass="w-full"></p-message>
    }

    <!-- Loading state -->
    @if (card.loading) {
        <div class="flex justify-content-center align-items-center p-4 gap-2">
            <p-progressSpinner styleClass="w-3rem h-3rem" strokeWidth="4"></p-progressSpinner>
            <span class="text-color-secondary">Daten werden geladen…</span>
        </div>
    }

    @if (!card.loading && card.executed && !card.errorMessage) {
        <div class="flex flex-column gap-3">

            <!-- (a) The exact QL query DTO sent -->
            @if (card.query) {
                <p-panel header="Gesendete QL-Abfrage" [toggleable]="true">
                    <pre class="xx-pg-pre">{{ formatQuery(card.query) }}</pre>
                </p-panel>
            }

            <!-- (b) Result table with dynamic columns -->
            @if (card.rows.length === 0) {
                <p-message
                        severity="info"
                        text="Keine Datensätze für diese Abfrage gefunden."
                        styleClass="w-full"></p-message>
            } @else {
                <p-table [value]="card.rows" [scrollable]="true" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr>
                            @for (col of card.columns; track col.key) {
                                <th>{{ col.header }}</th>
                            }
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            @for (col of card.columns; track col.key) {
                                <td>{{ displayValue(row, col.key) }}</td>
                            }
                        </tr>
                    </ng-template>
                </p-table>
            }

            <!-- (c) Collapsible raw JSON response -->
            <p-panel header="Raw JSON (Backend-Antwort)" [toggleable]="true" [collapsed]="true">
                <pre class="xx-pg-pre">{{ formatRaw(card.raw) }}</pre>
            </p-panel>
        </div>
    }

    <!-- Initial hint before first run -->
    @if (!card.loading && !card.executed && !card.errorMessage) {
        <p-message
                severity="info"
                text="Auf „QL ausführen“ klicken, um die Abfrage zu starten und das Ergebnis anzuzeigen."
                styleClass="w-full"></p-message>
    }
</ng-template>
